import api from './api';

/**
 * Server-side printing (CUPS via the API). Port of V1's `lpr -P <installname>`.
 *
 * The printer list is a plain GET on purpose: cms.printers is user-maintained
 * (Table Maintenance > Printers) and lookupCache's cachedGet is the first suspect
 * whenever a dropdown shows stale options.
 */

export type PrinterType = 'LASER' | 'LABEL';

export interface Printer {
  installname: string;
  displayname: string;
  driveraccess: 'Y' | 'N';
}

export interface PrintDefaults {
  laser: string | null;
  label: string | null;
}

export interface PrintJobResult {
  job_id: number;
  printer: string;
  display_name: string;
  copies: number;
}

export interface PrintOptions {
  printer?: string | null;
  copies?: number;
}

/** Preview of copying V1 default printers into user_settings (admin). */
export interface SeedDefaultsPlan {
  users: number;
  active_laser: number;
  active_label: number;
  skipped_existing: number;
  skipped_inactive: Record<string, number>;
  to_write: number;
  lasers: number;
  labels: number;
  items: Array<{ email: string; key: string; printer: string }>;
}

export interface SeedDefaultsResult {
  written: number;
  lasers: number;
  labels: number;
}

const printingService = {
  listPrinters: async (type: PrinterType): Promise<Printer[]> =>
    (await api.get<Printer[]>('/printing/printers', { params: { type } })).data,

  getDefaults: async (): Promise<PrintDefaults> =>
    (await api.get<PrintDefaults>('/printing/defaults')).data,

  /** `''` clears a default; an omitted key is left alone. */
  setDefaults: async (patch: Partial<Record<'laser' | 'label', string>>): Promise<PrintDefaults> =>
    (await api.put<PrintDefaults>('/printing/defaults', patch)).data,

  /** Admin: one test sheet to a LASER queue (Table Maintenance > Printers). */
  printTestPage: async (printer: string): Promise<PrintJobResult> =>
    (await api.post<PrintJobResult>('/printing/test-page', { printer })).data,

  /** Admin: what the V1 default-printer import would write. Writes nothing. */
  seedDefaultsPlan: async (): Promise<SeedDefaultsPlan> =>
    (await api.get<SeedDefaultsPlan>('/printing/seed-defaults')).data,

  /** Admin: run the V1 default-printer import. Never overwrites a 360 choice. */
  seedDefaultsApply: async (): Promise<SeedDefaultsResult> =>
    (await api.post<SeedDefaultsResult>('/printing/seed-defaults')).data,

  /** POST to any page's `.../print` route. `endpoint` is the full relative path. */
  printTo: async (
    endpoint: string,
    opts: PrintOptions,
    extraBody: Record<string, unknown> = {},
  ): Promise<PrintJobResult> =>
    (await api.post<PrintJobResult>(endpoint, {
      printer: opts.printer ?? null,
      copies: opts.copies ?? 1,
      ...extraBody,
    })).data,
};

export default printingService;

/** The server's message out of an axios error, else a generic line. */
export function printErrorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return String((detail as { message: unknown }).message);
  }
  return 'Print failed. Check the printer and try again.';
}
